import { useRef, useState, type FormEvent, type ReactNode } from "react";
import { Button, Checkbox, Input, SignaturePad } from "@dearbill/shared";

export interface SignatureInput {
  /** Base64 PNG of the drawn signature, without the data-URL prefix. */
  signaturePngBase64: string;
  /** Trimmed. */
  typedName: string;
}

interface SignatureFormProps {
  /** The typed-name field's starting value. */
  initialName: string;
  /** The intent checkbox's label. */
  intentLabel: ReactNode;
  /**
   * Identifies the consent text the intent checkbox affirms. A change
   * unchecks the box, so the signer confirms the new text; the drawn
   * signature and typed name stay.
   */
  consentKey?: string | null | undefined;
  busy: boolean;
  /** Rendered above the submit button, e.g. a submit error. */
  notice?: ReactNode;
  onSubmit: (input: SignatureInput) => void;
}

/**
 * The member portal's signing controls: a drawn signature, a typed name and an
 * intent affirmation. Submit is enabled only with a stroke, a non-blank name
 * and the box checked, and stays disabled while `busy`.
 */
export function SignatureForm({
  initialName,
  intentLabel,
  consentKey = null,
  busy,
  notice,
  onSubmit,
}: SignatureFormProps): ReactNode {
  const [typedName, setTypedName] = useState(initialName);
  const [intent, setIntent] = useState(false);
  const [hasStroke, setHasStroke] = useState(false);
  const getPngRef = useRef<() => string>(() => "");
  const [seenConsentKey, setSeenConsentKey] = useState(consentKey);
  if (seenConsentKey !== consentKey) {
    setSeenConsentKey(consentKey);
    setIntent(false);
  }

  const canSubmit = hasStroke && typedName.trim().length > 0 && intent && !busy;

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({ signaturePngBase64: getPngRef.current(), typedName: typedName.trim() });
  };

  return (
    <form className="mt-8 space-y-6" onSubmit={handleSubmit} noValidate>
      <SignaturePad
        label="Draw your signature"
        clearLabel="Clear"
        ariaLabel="Signature drawing area"
        onChange={({ hasStroke: hs, toPngBase64 }) => {
          setHasStroke(hs);
          getPngRef.current = toPngBase64;
        }}
      />

      <Input
        label="Type your full legal name"
        required
        autoComplete="name"
        value={typedName}
        onChange={(e) => setTypedName(e.target.value)}
      />

      <Checkbox
        label={intentLabel}
        checked={intent}
        onChange={(e) => setIntent(e.target.checked)}
      />

      {notice}

      <Button type="submit" size="lg" className="w-full" disabled={!canSubmit}>
        {busy ? "Submitting…" : "Sign and submit"}
      </Button>
    </form>
  );
}
